import { createFileRoute, Outlet } from '@tanstack/react-router';
import { useIntentionalSignOut } from '@/hooks/use-intentional-sign-out';
import { useGetAgencyMe } from '@/api/generated/agency/agency';
import { setActiveOrgId } from '@/api/axios-instance';
import { AgencyPageState, AgencyShell } from '@/features/agency';
import { InShellRoutePending } from '@/components/layout/route-pending-fallback';
import { PageLoader } from '@/components/ui/page-loader';

export const Route = createFileRoute('/_auth/agency')({
  beforeLoad: ({ preload }) => {
    // Hovering a link preloads this route and runs beforeLoad; only a real visit may change the salon API calls go to.
    if (!preload) setActiveOrgId(null);
  },
  component: AgencyLayout,
});

function AgencyLayout() {
  const signOut = useIntentionalSignOut();
  const agencyMeQuery = useGetAgencyMe();
  const agencyMember = agencyMeQuery.data?.data ?? null;

  // Runs before the agency shell exists, so it keeps the boot splash's Hayven mark (no skeleton, no spinner card).
  if (agencyMeQuery.isLoading) return <PageLoader />;

  if (agencyMeQuery.isError) {
    return (
      <div className="min-h-dvh bg-clovie-canvas p-4">
        <AgencyPageState
          title="Couldn’t Open Agency Workspace"
          description="Check your connection and try again."
          onRetry={() => agencyMeQuery.refetch()}
        />
      </div>
    );
  }

  if (!agencyMember) {
    return (
      <div className="min-h-dvh bg-clovie-canvas p-4">
        <AgencyPageState
          title="Agency Access Not Available"
          description="This account is signed in, but it has no active salon or agency membership. Ask an administrator for access, or sign out and use a different account."
        />
        <div className="text-center">
          <button
            type="button"
            onClick={() => void signOut()}
            className="inline-flex h-11 items-center rounded-[var(--r-pill)] bg-clovie-primary px-5 text-sm font-semibold text-white hover:bg-clovie-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clovie-brand focus-visible:ring-offset-2"
          >
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  return (
    <AgencyShell isAdmin={agencyMember.role === 'ADMIN'}>
      <InShellRoutePending>
        <Outlet />
      </InShellRoutePending>
    </AgencyShell>
  );
}
